import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

function CollapsedTooltip({ enabled, label, children }) {
  const [pos, setPos] = useState(null);

  const show = (event) => {
    if (!enabled) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setPos({ top: rect.top + rect.height / 2, left: rect.right });
  };

  return (
    <div className="relative" onMouseEnter={show} onMouseLeave={() => setPos(null)} onFocus={show} onBlur={() => setPos(null)}>
      {children}
      {enabled && pos && (
        <div
          role="tooltip"
          className="pointer-events-none fixed z-[70] -translate-y-1/2 whitespace-nowrap rounded-lg bg-slate-900 px-2.5 py-1 text-xs font-medium text-white shadow-lg"
          style={{ top: pos.top, left: pos.left + 8 }}
        >
          {label}
        </div>
      )}
    </div>
  );
}

export default function SidebarNavItem({
  item,
  active = false,
  labelsVisible = true,
  badge = 0,
  onNavigate,
  isGroup = false,
  expanded = false,
  onToggle,
}) {
  const Icon = item.icon;
  const count = Number(badge) || 0;
  const showBadge = count > 0;
  const badgeText = count > 99 ? "99+" : String(count);
  const tip = showBadge ? `${item.label} (${badgeText})` : item.label;

  const className = `
    group/item flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-right text-sm font-medium transition-colors
    focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400
    ${labelsVisible ? "" : "justify-center px-1.5"}
    ${active
      ? "bg-indigo-50 text-indigo-700"
      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}
  `;

  const body = (
    <>
      <span className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${active ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}>
        <Icon size={18} />
        {!labelsVisible && showBadge && (
          <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white" />
        )}
      </span>
      <span className={`min-w-0 flex-1 truncate ${labelsVisible ? "" : "sr-only"}`}>{item.label}</span>
      {labelsVisible && showBadge && (
        <span className="min-w-[1.25rem] rounded-full bg-rose-500 px-1.5 py-0.5 text-center text-[10px] font-bold leading-4 text-white">
          {badgeText}
        </span>
      )}
      {isGroup && labelsVisible && (
        <ChevronDown size={16} className={`shrink-0 text-slate-400 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
      )}
    </>
  );

  if (isGroup) {
    return (
      <CollapsedTooltip enabled={!labelsVisible} label={tip}>
        <button
          type="button"
          className={className}
          onClick={onToggle}
          aria-expanded={expanded}
          title={labelsVisible ? undefined : tip}
          data-nav-id={item.id}
        >
          {body}
        </button>
      </CollapsedTooltip>
    );
  }

  return (
    <CollapsedTooltip enabled={!labelsVisible} label={tip}>
      <Link
        to={item.path}
        className={className}
        onClick={onNavigate}
        aria-current={active ? "page" : undefined}
        title={labelsVisible ? undefined : tip}
        data-nav-id={item.id}
      >
        {body}
      </Link>
    </CollapsedTooltip>
  );
}
